import {Apollo, QueryRef} from 'apollo-angular';
import {ApolloError, MutationOptions, PureQueryOptions, QueryOptions, WatchQueryOptions} from '@apollo/client/core';
import {Injectable} from '@angular/core';
import {EmptyObject} from 'apollo-angular/types';
import {ErrorFabricService} from 'app/core/error-handler/error-fabric.service';
import {InternalError} from 'app/core/error-handler/errors/internal-error';
import {Sentry} from 'app/core/error-handler/sentry.service';
import {DemoService} from 'app/core/services/demo.service';
import {ApiCacheHelper} from 'app/shared/modules/api/helpers/api-cache-helper';
import {FragmentHelper} from 'app/shared/modules/api/helpers/fragment-helper';
import {combineLatest, Observable, of, throwError, timer} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {boolDevFlag, DevFlags} from 'test-helpers/dev-flags';
import {AuthServiceHelper} from 'app/core/services/auth.service.helper';

export class WatchQuery<T> {
    public valueChanges: Observable<T>;

    constructor(private queryRef: QueryRef<T>) {
        this.valueChanges = queryRef.valueChanges.pipe(
            // ignore partial event
            // Means data can be empty
            filter(v => !v.partial),
            map(r => r.data),
            catchError((err: ApolloError) => throwError(<InternalError>err.networkError))
        );
    }

    result(): Promise<Observable<T>> {
        return this.queryRef.result().then(
            () => this.valueChanges,
            (err: ApolloError) => {
                throw <InternalError>err.networkError;
            }
        );
    }

    refetch(variables?: EmptyObject): Promise<T> {
        return this.queryRef.refetch(variables).then(
            ({data}) => data,
            (err: ApolloError) => {
                throw <InternalError>err.networkError;
            }
        );
    }
}

export interface ApiQueryOptions<V> extends QueryOptions<V> {
    cacheHours?: number;
}

export type ApiWatchQueryOptions<V> = WatchQueryOptions<V>;

export interface ApiMutationOptions<T, V> extends MutationOptions<T, V> {
    skipBeforeMutationValidation?: boolean;
    refetchQueriesSync?: PureQueryOptions[] | PureQueryOptions;
}

@Injectable()
export class ApiService {
    private fragmentHelper = new FragmentHelper();

    constructor(
        private apollo: Apollo,
        private sentry: Sentry,
        private demo: DemoService,
        private errorFactory: ErrorFabricService,
        private localStorage: LocalStorageService,
        private auth: AuthServiceHelper
    ) {}

    public cacheHelper = new ApiCacheHelper(this.fragmentHelper, this.localStorage, this.auth);

    beforeMutation<T>(options: ApiMutationOptions<any, any>): void | Observable<T> {
        if (options.skipBeforeMutationValidation) {
            return;
        }
        if (this.isDemo()) {
            return timer(100).pipe(switchMap(() => throwError(this.errorFactory.createDemoAccountRequestError())));
        }
    }

    isDemo(): boolean {
        return this.demo.isDemoUser;
    }

    registerFragments(...fragments) {
        fragments.forEach(item => this.fragmentHelper.registerFragments(item));
    }

    watchQuery<T, V = EmptyObject>(options: ApiWatchQueryOptions<V>): WatchQuery<T> {
        this.breadCrumbs(options, 'watchQuery');
        return new WatchQuery(this.apollo.watchQuery(this.fragmentHelper.injectKnownFragments(options, 'query')));
    }

    query<T, V = EmptyObject>(options: ApiQueryOptions<V>): Observable<T> {
        this.breadCrumbs(options, 'query');
        const cache = this.cacheHelper.getFromCache(options);
        if (cache) {
            return of(cache) as Observable<T>;
        }

        return this.apollo.query<T, V>(this.fragmentHelper.injectKnownFragments(options, 'query')).pipe(
            map(r => r.data),
            tap(resp => (options.cacheHours ? this.cacheHelper.cacheHours(options, options.cacheHours, resp) : null)),
            catchError((err: ApolloError) => throwError(<InternalError>err.networkError))
        );
    }

    mutate<T, V = EmptyObject>(options: ApiMutationOptions<T, V>): Observable<T> {
        const beforeMutationError = this.beforeMutation<T>(options);
        if (beforeMutationError) {
            return beforeMutationError as Observable<T>;
        }
        this.breadCrumbs(options, 'mutation');
        const mutation = this.apollo.mutate<T, V>(this.fragmentHelper.injectKnownFragments(options, 'mutation')).pipe(
            map(r => r.data),
            catchError((err: ApolloError) => throwError(<InternalError>err.networkError))
        );

        if (options.refetchQueriesSync) {
            const refetchQueriesSync: PureQueryOptions[] = Array.isArray(options.refetchQueriesSync)
                ? options.refetchQueriesSync
                : [options.refetchQueriesSync];

            return combineLatest([
                mutation,
                ...refetchQueriesSync.map(refetchQueryOptions =>
                    this.query({
                        ...refetchQueryOptions,
                        fetchPolicy: 'network-only',
                    })
                ),
            ]).pipe(map(([mutationResult]) => <T>mutationResult)) as Observable<T>;
        }

        return mutation as Observable<T>;
    }

    breadCrumbs(options, opType: string) {
        const operations = options.query || options.mutation;
        operations.definitions
            .filter(op => op.kind === 'OperationDefinition')
            .forEach(op =>
                this.sentry.leaveBreadCrumbs({
                    category: boolDevFlag(DevFlags.LogGraphQLRequest, false, this.localStorage)
                        ? `GraphQL ${opType} ${op.name ? op.name.value : operations.loc.source}`
                        : 'GraphQL',
                    message: boolDevFlag(DevFlags.LogGraphQLRequest, false, this.localStorage)
                        ? debugGraphQLRequest(options.variables, operations.loc.source.body)
                        : `[${opType}] ${op.name ? op.name.value : operations.loc.source}`,
                })
            );
    }
}

function debugGraphQLRequest(variables, operationText) {
    return [
        '',
        '',
        '# ------------------------------------',
        '',
        operationText,
        '',
        '# ------------------------------------',
        '',
        // @ts-ignore
        JSON.stringify(variables, 4, 4),
        '',
        '# ------------------------------------',
        '',
    ].join('\n');
}
